interface LobuLocation {
	hostname: string;
	port: string;
	search?: string;
}

const SCHEME_QUERY_KEY = "lobu_scheme";
const SCHEME_STORAGE_KEY = "lobu.urlScheme";
type LobuScheme = "lobu" | "lobu-debug";

function validScheme(value: string | null): LobuScheme | null {
	return value === "lobu" || value === "lobu-debug" ? value : null;
}

function rememberedScheme(): LobuScheme | null {
	if (typeof window === "undefined") return null;
	try {
		return validScheme(window.sessionStorage.getItem(SCHEME_STORAGE_KEY));
	} catch {
		return null;
	}
}

function rememberScheme(scheme: LobuScheme): void {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(SCHEME_STORAGE_KEY, scheme);
	} catch {}
}

/**
 * The installed app owns `lobu://`; a native-injected marker identifies
 * pages opened by the isolated Xcode runner and targets `lobu-debug://`.
 * Unmarked cloud and local pages always target the signed app.
 */
export function lobuDeepLinkScheme(
	location: LobuLocation = window.location,
): LobuScheme {
	const explicit = validScheme(
		new URLSearchParams(location.search ?? "").get(SCHEME_QUERY_KEY),
	);
	if (explicit) {
		rememberScheme(explicit);
		return explicit;
	}
	const remembered = rememberedScheme();
	if (remembered) return remembered;
	return "lobu";
}

/** Remove only the internal scheme marker without re-encoding other values. */
export function stripLobuSchemeMarker(search: string): string {
	if (!search.startsWith("?")) return search;
	const segments = search.slice(1).split("&");
	const kept = segments.filter((segment) => {
		const separator = segment.indexOf("=");
		const encodedName = separator >= 0 ? segment.slice(0, separator) : segment;
		try {
			return decodeURIComponent(encodedName.replace(/\+/g, " ")) !== SCHEME_QUERY_KEY;
		} catch {
			return true;
		}
	});
	return kept.length > 0 ? `?${kept.join("&")}` : "";
}

export function lobuDeepLink(
	target: string,
	location: LobuLocation = window.location,
): string {
	return `${lobuDeepLinkScheme(location)}://${target.replace(/^\/+/, "")}`;
}
